import type { QueryResult } from '@apollo/client';
import { useLazyQuery, useQuery } from '@apollo/client';
import type { VariablesOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import _ from 'lodash';
import type { Channel } from 'phoenix';
import queryString from 'query-string';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from 'react';
import authTokenUtil from '../account/authTokenUtil';
import visitorAuthTokenUtil from '../account/visitorAuthTokenUtil';
import useAppSelector from '../app/redux/useAppSelector';
import LoadingIndicator from '../components/LoadingIndicator';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import type { SearchViewExternalResultsQuery } from '../graphql/generated/graphql';
import { AskSourceType, SearchFilterType } from '../graphql/generated/graphql';
import { SEARCH_PLATFORM_INFO_MAP } from '../integrations/IntegrationPlatformInfo';
import { parseSearchQuery } from '../topbar/quillFacets';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import Sizes from '../utils/responsive/Sizes';
import { useIsSize } from '../utils/responsive/isSize';
import { useLocation } from '../utils/router/v3Bridge';
import { isAiQuestion } from '../utils/search/aiAsk';
import AiAskAnswer from './AiAskAnswer';
import AiAskWidget from './AiAskWidget';
import SearchManagePlatformButton from './SearchManagePlatformButton';
import styles from './SearchView.module.scss';
import SearchViewExternalResults from './SearchViewExternalResults';
import SearchViewFilterMenu from './SearchViewFilterMenu';
import SearchViewHeader from './SearchViewHeader';
import SearchViewSlabResults from './SearchViewSlabResults';
import getSearchResultType, {
  filterBySearchResultType,
} from './getSearchResultType';
import useIsAiAskEnabled from './useIsAiAskEnabled';

const ENCOURAGE_INSTALLATION_PLATFORMS = ['google-drive', 'slack'];

const PREVIEW_SLAB_RESULT_COUNT = 10;
const FULL_SLAB_RESULT_COUNT = 50;
const SLAB_RESULT_PAGE_COUNT = 25;

const SLAB_SEARCH_TYPES_QUERY = gql(`
  query SearchViewSlabSearchTypes(
    $query: String!
    $filter: SearchFilter
  ) {
    searchTypes(
      query: $query
      filter: $filter
    ) {
      ...SearchViewHeader_searchType
    }
  }
`);

const KEYWORD_EXTRACTION_QUERY = gql(`
  query SearchViewKeywordExtraction($text: String!) {
    keywords(text: $text)
  }
`);

const SLAB_SEARCH_QUERY = gql(`
  query SearchViewSlabSearch(
    $query: String!
    $first: Int!
    $afterCursor: String
    $filter: SearchFilter
  ) {
    search(
      query: $query
      first: $first
      after: $afterCursor
      filter: $filter
    ) {
      edges {
        node {
          ...SearchViewSlabResults_results
        }
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      queryId
    }
  }
`);

export type SearchVariables = Omit<
  VariablesOf<typeof SLAB_SEARCH_QUERY>,
  'first'
>;

const typeMap = _.invert({
  [SearchFilterType.Post]: 'post',
  [SearchFilterType.User]: 'user',
  [SearchFilterType.Comment]: 'comment',
  [SearchFilterType.Template]: 'template',
  [SearchFilterType.Topic]: 'topic',
  [SearchFilterType.Group]: 'group',
} satisfies Record<SearchFilterType, string>);

const useSlabSearchQuery = ({
  variables,
  type,
  skip,
}: {
  variables: SearchVariables;
  type?: string;
  skip?: boolean;
}) => {
  const filter = useMemo(() => {
    const mappedType = type
      ? (typeMap[type] as SearchFilterType | undefined)
      : null;
    if (!mappedType) return variables.filter;
    return {
      ...variables.filter,
      types: [mappedType],
    };
  }, [type, variables.filter]);

  const response = useQuery(SLAB_SEARCH_QUERY, {
    fetchPolicy: 'network-only',
    skip,
    variables: {
      ...variables,
      first: PREVIEW_SLAB_RESULT_COUNT,
      filter,
    },
  });
  const [hasInitiallyFetchedMore, setHasInitiallyFetchedMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [previousVariables, setPreviousVariables] =
    useState<SearchVariables | null>(null);

  if (!_.isEqual(variables, previousVariables)) {
    setPageNumber(0);
    setHasInitiallyFetchedMore(false);
    setPreviousVariables(variables);
  }

  useEffect(() => {
    if (response.loading || hasInitiallyFetchedMore) return;
    const fetchMoreQuery = ConnectionHelper.generateFetchMore(
      SLAB_SEARCH_QUERY,
      response,
      'search',
      {
        afterCursorOffset: PREVIEW_SLAB_RESULT_COUNT - 1,
      },
    );

    if (fetchMoreQuery) {
      fetchMoreQuery({
        first: FULL_SLAB_RESULT_COUNT - PREVIEW_SLAB_RESULT_COUNT,
      }).finally(() => {
        setHasInitiallyFetchedMore(true);
      });
    } else {
      setHasInitiallyFetchedMore(true);
    }
  }, [response, hasInitiallyFetchedMore]);

  const fetchMoreQuery = ConnectionHelper.generateFetchMore(
    SLAB_SEARCH_QUERY,
    response,
    'search',
    {
      afterCursorOffset:
        FULL_SLAB_RESULT_COUNT + SLAB_RESULT_PAGE_COUNT * pageNumber - 1,
    },
  );
  const fetchMore: typeof fetchMoreQuery =
    fetchMoreQuery &&
    (async (...args) => {
      const results = await fetchMoreQuery(...args);
      setPageNumber((currentPageNumber) => currentPageNumber + 1);
      return results;
    });

  const previewResults = useMemo(
    () =>
      !response.loading
        ? _.compact(response.data?.search?.edges?.map((edge) => edge?.node))
        : [],
    [response.loading, response.data?.search?.edges],
  );

  return {
    data: previewResults,
    hasMoreResults: !!response?.data?.search?.pageInfo.hasNextPage,
    loading: response.loading,
    hasInitiallyFetchedMore,
    fetchMore:
      fetchMore && (() => fetchMore({ first: SLAB_RESULT_PAGE_COUNT })),
    queryId: response.data?.search?.queryId ?? undefined,
  };
};

const EXTERNAL_SEARCH_QUERY = gql(`
  query SearchViewExternalResults($platform: String!, $query: String!) {
    externalSearch(platform: $platform, query: $query) {
      id
      ...SearchViewExternalResults_mention
    }
  }
`);

const USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT = gql(`
  fragment useExternalSearchQuery_platform on ExternalSearchPlatform {
    platform
    personalAuthorized
    teamAuthorized
  }
`);

const useExternalSearchQuery = (
  searchQuery: string,
  platform?: DocumentType<typeof USE_EXTERNAL_SEARCH_QUERY_PLATFORM_FRAGMENT>,
  type?: string,
) => {
  const {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    personalAuthorized,
    platform: platformKey = '',
    // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
    teamAuthorized,
  } = platform ?? {};

  const [externalResults, setExternalResults] = useState<
    Record<
      string,
      Pick<
        QueryResult<
          SearchViewExternalResultsQuery,
          { platform: string; query: string }
        >,
        'data' | 'error'
      >
    >
  >({});

  const platformInfo = SEARCH_PLATFORM_INFO_MAP[platformKey];
  const isAuthorized = personalAuthorized || teamAuthorized;

  const [loadExternalSearch] = useLazyQuery(EXTERNAL_SEARCH_QUERY);

  const loadPlatformData = async (
    query: string,
    externalPlatformKey: string,
  ) => {
    const res = await loadExternalSearch({
      variables: { query, platform: externalPlatformKey },
      fetchPolicy: 'network-only',
    });
    setExternalResults((d) => ({
      ...d,
      [externalPlatformKey]: { data: res.data, error: res.error },
    }));
    return { count: res.data?.externalSearch?.length ?? null };
  };

  const [previousSearchQuery, setPreviousSearchQuery] = useState(searchQuery);
  if (searchQuery !== previousSearchQuery) {
    setPreviousSearchQuery(searchQuery);
    setExternalResults({});
  }

  const { data, error } = externalResults[platformKey] ?? {};

  const results =
    platformInfo && isAuthorized ? data?.externalSearch ?? [] : [];
  const filteredResults = _.compact(filterBySearchResultType(type, results));

  return {
    results,
    filteredResults,
    error,
    loading: !!platformKey && !externalResults[platformKey],
    loadPlatformData,
  };
};

const EXTERNAL_PLATFORMS_QUERY = gql(`
  query SearchViewExternalPlatforms($redirectPath: String!) {
    externalSearchPlatforms(redirectPath: $redirectPath) {
      ...useExternalSearchQuery_platform
      ...SearchManagePlatformButton_platforms
      ...SearchViewHeader_platforms
      ...SearchViewExternalResults_platform
    }
  }
`);

const useExternalSearchPlatforms = () => {
  const userType = useAppSelector((state) => state.session.user.type);

  const location = useLocation();
  const redirectSearch = { q: location.query.q, platform: '$PLATFORM' };
  const searchString = `?${queryString.stringify(redirectSearch)}`;
  const redirectPath = `${location.pathname}${searchString}`;

  const { data, loading, refetch } = useQuery(EXTERNAL_PLATFORMS_QUERY, {
    fetchPolicy: 'network-only',
    variables: { redirectPath },
  });

  const externalSearchPlatforms = data?.externalSearchPlatforms;

  return useMemo(() => {
    const results = externalSearchPlatforms?.map((platform) => ({
      ...platform,
      teamAuthorized: userType !== 'GUEST' ? platform.teamAuthorized : false,
    }));

    return { loading, data: results, refetch };
  }, [externalSearchPlatforms, loading, refetch, userType]);
};

const useEncourageInstallationSet = (
  platforms?: { personalAuthorized?: boolean; teamAuthorized?: boolean }[],
) => {
  const dismissedSearchConnectionPrompts = useAppSelector(
    (state) => state.ui.dismissedSearchConnectionPrompts,
  );

  return useMemo(() => {
    if (
      !platforms ||
      platforms.some((p) => p.personalAuthorized || p.teamAuthorized)
    ) {
      return new Set([]);
    }

    return new Set(
      _.without(
        ENCOURAGE_INSTALLATION_PLATFORMS,
        ...dismissedSearchConnectionPrompts,
      ),
    );
  }, [dismissedSearchConnectionPrompts, platforms]);
};

type AiAskSource = {
  id: string;
  primary: string;
  snippet: string;
  type: AskSourceType;
};
export type AiAskStreamResult = {
  threadId: string;
  answer: string;
  sources: AiAskSource[];
};

const AI_ASK_SOURCE_TYPE_MAPPER: Record<string, AskSourceType> = {
  post: AskSourceType.Post,
};

const mapSources = (sources: AiAskSource[]) =>
  _.compact(
    sources.map((source) => {
      const type = AI_ASK_SOURCE_TYPE_MAPPER[source.type.toLowerCase()];
      return type ? { ...source, type } : null;
    }),
  );

const useAiAskStream = ({
  query,
  disabled,
}: {
  query: string;
  disabled: boolean;
}) => {
  const socket = useAppSelector((state) => state.session.socket);
  const channelRef = useRef<Channel | null>(null);
  const [value, setValue] = useState<{
    loading: boolean;
    response: AiAskStreamResult | null;
  }>({ loading: false, response: null });

  const getOrSetupChannel = useCallback(() => {
    if (!socket || channelRef.current) {
      return channelRef.current;
    }

    channelRef.current = socket.channel('ask:lobby', {
      guardian_token: authTokenUtil.get() ?? visitorAuthTokenUtil.get(),
    });

    channelRef.current.on(
      'answer_started',
      (payload: { thread_id: string }) => {
        setValue((oldValue) => {
          if (!oldValue.loading) {
            return oldValue;
          }
          return {
            response: { threadId: payload.thread_id, answer: '', sources: [] },
            loading: true,
          };
        });
      },
    );
    channelRef.current.on(
      'answer_partial',
      (payload: {
        text: string;
        thread_id: string;
        sources: AiAskSource[];
      }) => {
        setValue((oldValue) => {
          if (
            !oldValue.response ||
            oldValue.response.threadId !== payload.thread_id
          ) {
            return oldValue;
          }
          return {
            response: {
              threadId: payload.thread_id,
              answer: oldValue.response.answer + payload.text,
              sources: _.uniqBy(
                [...oldValue.response.sources, ...mapSources(payload.sources)],
                'id',
              ),
            },
            loading: false,
          };
        });
      },
    );
    channelRef.current.on(
      'answer_full',
      (payload: {
        answer: string;
        thread_id: string;
        sources: AiAskSource[];
      }) => {
        setValue((oldValue) => {
          if (
            oldValue.response &&
            oldValue.response.threadId !== payload.thread_id
          ) {
            return oldValue;
          }
          return {
            response: {
              threadId: payload.thread_id,
              answer: payload.answer,
              sources: mapSources(payload.sources),
            },
            loading: false,
          };
        });
      },
    );

    channelRef.current.join();

    return channelRef.current;
  }, [setValue, socket]);

  useEffect(() => {
    if (disabled) {
      setValue({ loading: false, response: null });
      return;
    }
    const channel = getOrSetupChannel();
    if (!channel) {
      setValue({ loading: false, response: null });
      return;
    }
    setValue({ loading: true, response: null });

    channel.push('ask', { query });
  }, [disabled, query, getOrSetupChannel]);

  useEffect(
    () => () => {
      channelRef.current?.leave();
      channelRef.current = null;
    },
    [],
  );

  return value;
};

const useAiAsk = ({
  searchInput,
  askMode,
  disabled,
}: {
  searchInput: string;
  askMode: boolean;
  disabled: boolean;
}) => {
  const isQuestion = isAiQuestion(searchInput);
  const isWidgetAllowed = !disabled && !askMode && isQuestion;

  const { response, loading } = useAiAskStream({
    query: searchInput,
    disabled: disabled || !askMode || !isQuestion,
  });

  const [isAskWidgetOpen, setIsAskWidgetOpen] = useState(isWidgetAllowed);

  const [previousSearchInput, setPreviousSearchInput] = useState(searchInput);
  if (searchInput !== previousSearchInput) {
    setPreviousSearchInput(searchInput);
    setIsAskWidgetOpen(isWidgetAllowed);
  }

  if (!isWidgetAllowed && isAskWidgetOpen) {
    setIsAskWidgetOpen(false);
  }

  return {
    aiAskData: response,
    isAiAskLoading: loading,
    isAiAskWidgetOpen: isAskWidgetOpen,
    closeAiAskWidget: () => setIsAskWidgetOpen(false),
  };
};

interface SearchViewProps {
  initialPlatform?: string;
  initialType?: string;
}

const SearchView = ({ initialPlatform, initialType }: SearchViewProps) => {
  const [, startTransition] = useTransition();
  const isDesktop = useIsSize(Sizes.LG);
  const location = useLocation();
  const [tab, setTab] = useState({
    platform: initialPlatform,
    type: initialType,
  });

  const setPlatform = (key?: string) => {
    if (key === tab.platform) {
      return;
    }

    startTransition(() => {
      setTab({ platform: key, type: undefined });
    });
  };

  const setType = (key?: string) => {
    if (key === tab.type) {
      return;
    }

    startTransition(() => {
      setTab((currentTab) => ({ platform: currentTab.platform, type: key }));
    });
  };

  const {
    authError,
    q: searchInput = '',
    mode,
  } = location.query as { authError?: string; q?: string; mode?: 'ask' };

  const isAskMode = mode === 'ask';
  const isAiAskEnabled = useIsAiAskEnabled();

  const platformKey =
    tab.platform && SEARCH_PLATFORM_INFO_MAP[tab.platform]?.platform;

  const parsedVariables = useMemo(
    () => parseSearchQuery(searchInput),
    [searchInput],
  );

  const keywordResponse = useQuery(KEYWORD_EXTRACTION_QUERY, {
    fetchPolicy: 'network-only',
    variables: { text: parsedVariables.query },
    skip: !isAskMode || !isAiAskEnabled,
  });

  const keywords = keywordResponse.data?.keywords?.join(', ');

  const variables = useMemo(() => {
    if (keywordResponse.loading) {
      return { ...parsedVariables, query: '' };
    }
    return {
      ...parsedVariables,
      query: keywords || parsedVariables.query,
    };
  }, [keywordResponse, keywords, parsedVariables]);

  const { aiAskData, isAiAskLoading, isAiAskWidgetOpen, closeAiAskWidget } =
    useAiAsk({
      searchInput: parsedVariables.query,
      askMode: isAskMode,
      disabled: !isAiAskEnabled || !!tab.type || !!platformKey,
    });

  useEffect(() => {
    if (mode === 'ask') {
      setTab({ platform: undefined, type: undefined });
    }
  }, [searchInput, mode]);

  const { data: searchTypesData } = useQuery(SLAB_SEARCH_TYPES_QUERY, {
    variables,
    skip: !variables.query,
  });

  const slabAllResponse = useSlabSearchQuery({
    variables,
    skip: !variables.query,
  });

  const slabFilteredResponse = useSlabSearchQuery({
    variables,
    type: tab.type,
    skip: !variables.query || !!platformKey || !tab.type,
  });

  const { fetchMore, loading, data, queryId } = tab.type
    ? slabFilteredResponse
    : slabAllResponse;

  const { data: platforms, refetch: refetchPlatforms } =
    useExternalSearchPlatforms();
  const platform = platforms?.find((p) => p.platform === platformKey);
  const encouragedPlatformSet = useEncourageInstallationSet(platforms);

  const externalResponse = useExternalSearchQuery(
    variables.query,
    platform,
    tab.type,
  );

  const isSearching =
    keywordResponse.loading ||
    slabAllResponse.loading ||
    slabFilteredResponse.loading ||
    externalResponse.loading;

  if (!platformKey && !slabAllResponse) {
    return null;
  }

  const externalCountsByType = _.countBy(
    externalResponse.results,
    getSearchResultType,
  );

  return (
    <div id="content" className="no-padding-x">
      <div
        className={classNames(styles.container, {
          [styles.showSidebar]: isDesktop,
        })}
      >
        <div className={styles.title}>
          {(aiAskData || isAiAskLoading) && (
            <div className={styles.aiAnswer}>
              <AiAskAnswer query={parsedVariables.query} ask={aiAskData} />
            </div>
          )}
          <div className={styles.headerContainer}>
            <div
              className={classNames(styles.headerTitleContainer, {
                [styles.noKeywords]: !keywords,
              })}
            >
              <div className={styles.headerTitle}>
                <h2>Search results</h2>
                <div
                  className={classNames(styles.loading, {
                    [styles.loadingHidden]: !isSearching,
                  })}
                >
                  <LoadingIndicator delay={0} adaptColor />
                </div>
              </div>
              {isDesktop ? (
                <SearchViewFilterMenu />
              ) : (
                <SearchManagePlatformButton
                  platforms={platforms || []}
                  refetch={refetchPlatforms}
                />
              )}
            </div>
            {keywords && (
              <div className={styles.keywords}>Keywords: {keywords}</div>
            )}
          </div>
        </div>
        <div className={styles.header}>
          {isAiAskWidgetOpen && <AiAskWidget close={closeAiAskWidget} />}
          <SearchViewHeader
            activePlatformKey={platformKey}
            encouragedPlatformSet={encouragedPlatformSet}
            platforms={platforms || []}
            type={tab.type}
            setType={setType}
            setPlatform={setPlatform}
            externalCountsByType={externalCountsByType}
            vertical={isDesktop}
            searchTypes={searchTypesData?.searchTypes ?? null}
            verticalExtra={
              isDesktop ? (
                <div className={styles.verticalExtraContainer}>
                  <SearchManagePlatformButton
                    platforms={platforms || []}
                    refetch={refetchPlatforms}
                  />
                </div>
              ) : null
            }
            query={variables.query}
            loadPlatformData={externalResponse.loadPlatformData}
          />
        </div>
        <div className={styles.results}>
          {platformKey ? (
            platform && (
              <SearchViewExternalResults
                authError={authError}
                encouragedPlatformSet={encouragedPlatformSet}
                platform={platform}
                results={externalResponse.filteredResults}
                loading={externalResponse.loading}
                error={externalResponse.error}
              />
            )
          ) : (
            <SearchViewSlabResults
              data={data}
              loading={keywordResponse.loading || loading}
              fetchMore={fetchMore}
              queryId={queryId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchView;
